import { InventoryGrid } from "@/components/game/InventoryGrid";
import { useGame } from "@/context/GameContext";
import { ITEM_ICON, itemName, SKILL_LABEL } from "@/lib/gameData";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Home, Hammer, Coins, Lock, Check } from "lucide-react";

export function InventoryPanel() {
  return (
    <div className="p-4 lg:p-6" data-testid="inventory-panel">
      <div className="mb-4">
        <h2 className="font-display text-3xl heading-gilded mb-1">Backpack</h2>
        <p className="text-stone-500 font-body">28 slots. Double-click a weapon to equip.</p>
      </div>
      <InventoryGrid />
    </div>
  );
}

export function HousingPanel() {
  const { snap, content, buildRoom, changeTheme } = useGame();
  const rooms = content?.rooms || {};
  const themes = content?.themes || {};
  const items = content?.items || {};
  const house = snap?.house || { theme: "english_cottage", rooms: [] };
  const conLv = snap?.skills?.construction?.level ?? 1;
  const gold = snap?.gold ?? 0;

  const builtSet = new Set((house.rooms || []).map((r) => r.type));
  const currentTheme = themes[house.theme] || {};
  const themeImg = `/generated/theme_${house.theme}.png`;

  async function tryBuild(roomType) {
    try { await buildRoom(roomType); toast.success(`${rooms[roomType].name} built!`); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function tryTheme(themeId) {
    try { await changeTheme(themeId); toast.success(`Redecorated: ${themes[themeId].name}`); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  function playerHasMaterials(cost) {
    const inv = snap?.inventory || [];
    return cost.every(([id, qty]) => (inv.find((s) => s.item === id)?.qty || 0) >= qty);
  }

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="housing-panel">
      {/* Header banner */}
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.35) 0%, rgba(12,10,9,0.9) 100%), url(${themeImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="p-8 min-h-[220px] flex flex-col justify-end">
          <div className="uppercase tracking-[0.3em] text-amber-500 font-numeric text-[10px] mb-1">
            {currentTheme.region}
          </div>
          <h2 className="font-display text-4xl heading-gilded mb-1" data-testid="house-theme-name">
            {currentTheme.name}
          </h2>
          <p className="text-stone-300 font-body max-w-lg">{currentTheme.description}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-xs font-numeric text-stone-400">
            <span><Hammer size={12} className="inline mr-1 text-amber-500" /> Construction LV <span className="text-amber-300">{conLv}</span></span>
            <span>Rooms built: <span className="text-amber-300">{house.rooms.length}</span> / {Object.keys(rooms).length}</span>
            <span>Bank capacity: <span className="text-amber-300">{snap?.bank_capacity}</span></span>
          </div>
        </div>
      </div>

      {/* Themes */}
      <section>
        <div className="flex items-center gap-4 mb-3">
          <h3 className="font-display text-xl heading-gilded uppercase tracking-widest">Regional themes</h3>
          <div className="flex-1 divider-rune" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(themes).map(([id, t]) => {
            const locked = conLv < t.level;
            const active = house.theme === id;
            const affordable = gold >= (t.unlock_cost || 0);
            return (
              <button
                key={id}
                onClick={() => tryTheme(id)}
                disabled={locked || (!active && !affordable)}
                data-testid={`theme-${id}`}
                className={
                  "panel overflow-hidden text-left group transition-all duration-200 " +
                  (active ? "border-amber-500 ring-1 ring-amber-500/40" :
                    locked ? "opacity-40 cursor-not-allowed" : "hover:border-amber-700")
                }
              >
                <div
                  className="h-24 w-full"
                  style={{
                    backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.2), rgba(12,10,9,0.7)), url(/generated/theme_${id}.png)`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg leading-tight">{t.name}</div>
                    {active && <Check size={14} className="text-amber-400" />}
                    {locked && <Lock size={12} className="text-stone-500" />}
                  </div>
                  <div className="text-[10px] text-stone-500 font-numeric uppercase tracking-widest mt-0.5">
                    {t.region}
                  </div>
                  <div className="text-xs mt-1 font-numeric text-stone-400 flex items-center gap-2">
                    <span>LV {t.level}</span>
                    {t.unlock_cost > 0 && (
                      <span className="flex items-center gap-0.5">
                        <Coins size={10} className="text-amber-500" />{t.unlock_cost.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Rooms */}
      <section>
        <div className="flex items-center gap-4 mb-3">
          <h3 className="font-display text-xl heading-gilded uppercase tracking-widest">Rooms</h3>
          <div className="flex-1 divider-rune" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Object.entries(rooms).map(([id, r]) => {
            const built = builtSet.has(id);
            const locked = conLv < r.level;
            const canAfford = gold >= r.cost_gold && playerHasMaterials(r.cost_items || []);
            return (
              <article
                key={id}
                className={
                  "panel p-4 flex flex-col gap-3 " +
                  (built ? "border-emerald-800/70" : locked ? "opacity-50" : "")
                }
                data-testid={`room-${id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Home size={16} className="text-amber-500" />
                      <h4 className="font-display text-lg heading-gilded">{r.name}</h4>
                      {built && (
                        <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-numeric ml-1">
                          Built
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-stone-400 font-body mt-1">{r.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-numeric text-xs text-stone-500">LV {r.level}</div>
                    <div className="font-numeric text-xs text-amber-400">+{r.xp} XP</div>
                  </div>
                </div>
                {!built && (
                  <div className="mt-1 text-xs font-body space-y-1">
                    <div className="flex items-center gap-2 text-stone-400">
                      <Coins size={12} className="text-amber-500" />
                      <span className={gold >= r.cost_gold ? "text-amber-300" : "text-red-400"}>
                        {r.cost_gold.toLocaleString()} gp
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-stone-400">
                      {(r.cost_items || []).map(([iid, iq]) => {
                        const have = (snap?.inventory || []).find((s) => s.item === iid)?.qty || 0;
                        const ok = have >= iq;
                        return (
                          <span key={iid} className={"flex items-center gap-1 " + (ok ? "text-stone-300" : "text-red-400")}>
                            <span>{ITEM_ICON[iid] || "◇"}</span>
                            <span className="font-numeric">{iq}</span>
                            <span className="text-xs">{itemName(iid, items)}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                {!built && (
                  <button
                    disabled={locked || !canAfford}
                    onClick={() => tryBuild(id)}
                    data-testid={`build-${id}-btn`}
                    className={
                      "btn-gold text-sm px-3 py-1.5 mt-auto disabled:opacity-40 disabled:cursor-not-allowed"
                    }
                  >
                    {locked ? `Requires LV ${r.level}` : canAfford ? "Build room" : "Missing resources"}
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <p className="text-xs text-stone-500 font-body italic">
        Rooms grant permanent passive buffs. Bonuses stack (e.g., Study + Throne Room = +25% Magic XP).
        Level Construction via the Skills panel to unlock more rooms and richer themes.
      </p>
    </div>
  );
}
